import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { Transaction } from 'src/app/models/transaction';
import { AuthService } from 'src/app/services/auth.service';
import { TransactionsService } from 'src/app/services/transactions.service';
import { TicketService } from 'src/app/services/ticket.service';
import { BonsService } from 'src/app/services/bons.service';
import { TxTableBase } from '../shared/tx-table.base';

// Même préfixe que Compliance.PREFIX_BON_MARCHAND côté contrat — un tokenId
// de bon marchand n'est jamais dans `tickets` (titres B2B/B2C classiques),
// il faut le résoudre via le catalogue de bons du marchand à la place.
function isVoucherTokenId(id: any): boolean {
  try {
    return (BigInt(String(id).trim()) >> 224n) === 1n;
  } catch {
    return false;
  }
}

interface VoucherDenominationLookup {
  tokenId: string;
  label: string;
  faceValue: number;
}

@Component({
  selector: 'app-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['../shared/tx-table.css', './transactions.component.css'],
})
export class TransactionsComponent extends TxTableBase implements OnInit, OnDestroy {
  data: Transaction[] = [];
  voucherDenominations: VoucherDenominationLookup[] = [];
  account!: string;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private txService: TransactionsService,
    private authService: AuthService,
    private ticketService: TicketService,
    private bonsService: BonsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadTicketDataThenTransactions();
    this.loadVoucherDenominations();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // ---------- Bons marchands ----------

  private loadVoucherDenominations(): void {
    this.bonsService
      .getGroups()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => {
          this.voucherDenominations = (res.groups || []).flatMap((g: any) =>
            (g.denominations || [])
              .filter((d: any) => d.tokenId)
              .map((d: any) => ({ tokenId: d.tokenId, label: g.label, faceValue: d.faceValue })),
          );
          // Les libellés des bons sont maintenant connus : on recalcule la recherche / les filtres
          if (this.rows.length) this.applyFilters();
        },
        error: (error) => console.error('Failed to load voucher denominations for transactions table:', error),
      });
  }

  private resolveVoucherDenomination(id: any): VoucherDenominationLookup | undefined {
    return this.voucherDenominations.find((d) => String(d.tokenId) === String(id).trim());
  }

  /** Libellé de la colonne "Titre" (utilisé aussi par la recherche et le filtre Titre) */
  override ticketLabel(id: any): string {
    if (isVoucherTokenId(id)) {
      const denomination = this.resolveVoucherDenomination(id);
      return denomination ? denomination.label : 'Bon marchand';
    }
    return super.ticketLabel(id);
  }

  /**
   * Montant affiché. Pour un bon marchand, `amount` est une QUANTITÉ de bons (1, 2...),
   * pas une valeur en XOF comme pour un titre B2B/B2C classique — il faut la multiplier
   * par la faciale du bon concerné.
   */
  rowAmount(row: Transaction): number {
    const amount = Number(row.amount) || 0;
    if (isVoucherTokenId(row.ticketId)) {
      const denomination = this.resolveVoucherDenomination(row.ticketId);
      if (denomination) return amount * denomination.faceValue;
    }
    return amount;
  }

  // ---------- Chargement des transactions ----------

  private loadTicketDataThenTransactions(): void {
    this.ticketService
      .getAllTickets()
      .pipe(
        switchMap((re) => {
          this.tickets = re.tickets;
          return this.authService.getUser();
        }),
        switchMap((user) => {
          this.account = user?.account ?? '';
          return this.txService.getTransactions('merchant', user?.sub);
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (txs) => {
          this.data = txs;
          this.setRows(txs.filter((item) => item.type === 'Transfer'));
        },
        (error) => console.error(error),
      );
  }
}